import ContentCopy from '@mui/icons-material/ContentCopy'
import Delete from '@mui/icons-material/Delete'
import Edit from '@mui/icons-material/Edit'
import {
  Chip,
  Divider,
  ListItemIcon,
  ListItemText,
  MenuItem,
  Stack,
  Typography,
} from '@mui/material'
import { QueryKey } from '@tanstack/react-query'
import useOverlay from '@/hooks/useOverlay'
import { useDeletePosition } from '@/service-library/hooks/positions'
import useIsSuperUser from '@/services/hooks/useIsSuperUser'
import { SUPER_USER_ONLY_COLOR } from '@/theme/usePixydocsTheme'
import { PositionLookup } from '@/types/position-lookups'
import { Position } from '@/types/positions'
import { summarizePositionRule } from '@/utils/positions'
import {
  showErrorSnackbar,
  showInfoSnackbar,
  showSuccessSnackbar,
} from '@/utils/snackbars'
import copyToClipboard from '@/components/document-actions-menu/copy-to-clipboard'
import IconButtonMenu from '@/components/icon-button-menu/IconButtonMenu'
import DeletePositionDialog from './DeletePositionDialog'

type PositionRowProps = {
  position: Position
  lookup: PositionLookup | undefined
  startingPointLabel?: string
  listQueryKey: QueryKey
  onEdit: () => void
}

export default function PositionRow({
  position,
  lookup,
  startingPointLabel,
  listQueryKey,
  onEdit,
}: PositionRowProps) {
  const isSuperUser = useIsSuperUser()
  const menuOverlay = useOverlay()
  const deleteOverlay = useOverlay()

  const { deletePosition } = useDeletePosition({
    listQueryKey,
    onSuccess: () => {
      showSuccessSnackbar('Position Deleted')
    },
    onError: () => {
      showErrorSnackbar(
        "Couldn't delete this position. It's named by an approval policy, or it has already been used to approve a document.",
      )
    },
  })

  // Two positions can share a lookup and the same params and still find
  // different people, because one starts from the job on the document and the
  // other from the employee. Without the starting point here they read as
  // identical rows.
  const ruleSummary = [
    summarizePositionRule(position, lookup),
    startingPointLabel && `Starts from ${startingPointLabel}`,
  ]
    .filter(Boolean)
    .join(' · ')

  return (
    <>
      <Stack direction="row" alignItems="center" spacing={2} sx={{ py: 1.5 }}>
        <Stack spacing={0.25} sx={{ minWidth: 0, flex: 1 }}>
          <Typography noWrap>{position.name}</Typography>
          {ruleSummary && (
            <Typography variant="caption" color="text.secondary" noWrap>
              {ruleSummary}
            </Typography>
          )}
        </Stack>

        <Chip
          size="small"
          label={lookup?.name || position.lookup_code}
          sx={{ flexShrink: 0 }}
        />

        <IconButtonMenu
          overlay={menuOverlay}
          IconButtonProps={{
            size: 'small',
            'aria-label': `Actions for ${position.name}`,
          }}
        >
          <MenuItem
            onClick={() => {
              menuOverlay.close()
              onEdit()
            }}
          >
            <ListItemIcon>
              <Edit fontSize="small" />
            </ListItemIcon>
            <ListItemText>Edit position</ListItemText>
          </MenuItem>
          {isSuperUser && (
            <MenuItem
              onClick={() => {
                copyToClipboard(position.id)
                showInfoSnackbar('Copied to Clipboard')
                menuOverlay.close()
              }}
              sx={{ color: SUPER_USER_ONLY_COLOR }}
            >
              <ListItemIcon sx={{ color: SUPER_USER_ONLY_COLOR }}>
                <ContentCopy fontSize="small" />
              </ListItemIcon>
              <ListItemText>Copy ID</ListItemText>
            </MenuItem>
          )}
          <MenuItem
            onClick={() => {
              menuOverlay.close()
              deleteOverlay.open()
            }}
          >
            <ListItemIcon>
              <Delete fontSize="small" color="error" />
            </ListItemIcon>
            <ListItemText primaryTypographyProps={{ color: 'error' }}>
              Delete position
            </ListItemText>
          </MenuItem>
        </IconButtonMenu>
      </Stack>
      <Divider />

      <DeletePositionDialog
        overlay={deleteOverlay}
        positionName={position.name}
        onDelete={() => deletePosition(position.id)}
      />
    </>
  )
}
